import { useMemo, useState } from "react"
import { Button, Checkbox, ColorPicker, Modal } from "antd"
import { MdAdd, MdDelete, MdEdit, MdExpandMore, MdChevronRight } from "react-icons/md"
import { toast } from "react-toastify"
import BackButton from "../BackButton"
import {
    getProduction, addCustomCategory, renameCustomCategory, deleteCustomCategory,
    saveElement, deleteProductionElement,
} from "../../utils/productionUtils.js"

const Overlay = ({ children }) => (
    <div className='bg-[#00000030] z-[90] fixed top-0 left-0 h-[100vh] w-[100vw] flex justify-center items-center'>
        <div className='w-[400px] max-w-[95%] max-h-[90vh] overflow-y-auto bg-white rounded-lg p-4 text-[12px]'>{children}</div>
    </div>
)

const CategoryForm = ({ category, onSave, close }) => {
    const [name, setName] = useState(category?.name || '')
    const [color, setColor] = useState(category?.color || '#d9dee3')
    return <Overlay>
        <h2 className='w-full font-bold text-center text-[#868686] text-[14px]'>{category ? 'Edit category' : 'Add category'}</h2>
        <p className='text-[#868686] mt-6 mb-2'>Category name</p>
        <input value={name} onChange={e => setName(e.target.value)} className='border w-full p-2 rounded-lg' placeholder='Category name' />
        <div className='flex gap-2 items-center mt-4'>
            <ColorPicker size='small' defaultValue={color} onChange={(_, hex) => setColor(hex)} />
            <p className='text-[#868686]'>Category color</p>
        </div>
        <div className='flex justify-between mt-4'>
            <button onClick={close} className='px-3 py-2 border rounded-lg'>Cancel</button>
            <button onClick={() => onSave({ name, color })} className='px-3 py-2 bg-[#0099ff] rounded-lg text-white'>Save</button>
        </div>
    </Overlay>
}

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '')

const ElementForm = ({ element, category, categories, scenes, defaultScene, onSave, close }) => {
    const [name, setName] = useState(element?.name || '')
    const [notes, setNotes] = useState(element?.notes || '')
    const [categoryName, setCategoryName] = useState(element?.categoryName || category.name)
    const [selected, setSelected] = useState(element?.scenes || (defaultScene !== null ? [defaultScene] : []))
    const toggle = (i) => setSelected(s => s.includes(i) ? s.filter(x => x !== i) : [...s, i].sort((a, b) => a - b))
    return <Overlay>
        <h2 className='w-full font-bold text-center text-[#868686] text-[14px]'>{element ? 'Edit element' : 'Add element'}</h2>
        <p className='text-[#868686] mt-6 mb-2'>Category</p>
        <select inputMode='none' value={categoryName} onChange={e => setCategoryName(e.target.value)} className='border w-full p-2 rounded-lg bg-white'>
            {categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
        <p className='text-[#868686] mt-4 mb-2'>Element name</p>
        <input value={name} onChange={e => setName(e.target.value)} className='border w-full p-2 rounded-lg' placeholder='e.g. Guitar' />
        <p className='text-[#868686] mt-4 mb-2'>Item notes</p>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} className='border h-24 w-full p-2 rounded-lg' placeholder='Add notes' />
        <p className='text-[#868686] mt-4 mb-2'>Scenes</p>
        <div className='border rounded-lg max-h-[180px] overflow-y-auto'>
            {scenes.filter(s => s.number).map(s =>
                <label key={s.index} className='flex gap-2 items-center px-3 py-2 border-b last:border-b-0 cursor-pointer hover:bg-[#f3f3f3]'>
                    <Checkbox checked={selected.includes(s.index)} onChange={() => toggle(s.index)} />
                    <span className='text-[#4b4b4b] truncate'>Scene {s.number} — {s.title}</span>
                </label>)}
        </div>
        <div className='flex justify-between mt-4'>
            <button onClick={close} className='px-3 py-2 border rounded-lg'>Cancel</button>
            <button onClick={() => onSave({ name, notes, categoryName, scenes: selected })} className='px-3 py-2 bg-[#0099ff] rounded-lg text-white'>Save</button>
        </div>
    </Overlay>
}

const ProductionPanel = ({ setPanel, editor, sceneElements, setUpdateElements, permissionToEdit }) => {
    // sceneElements changes whenever the script changes, so recompute with it.
    const production = useMemo(() => getProduction(editor), [editor, sceneElements])
    // Same numbering as Storyboard / Dashboard pickers: headings in order, empty headings skipped.
    const scenes = useMemo(() => {
        let n = 0
        return production.scenes.map(s => {
            const title = (s.title || '').replace(/\s+/g, ' ').trim()
            return { ...s, title, number: title ? ++n : null }
        })
    }, [production])
    const numberOf = (i) => scenes[i]?.number ?? (i + 1)
    const [selectedScene, setSelectedScene] = useState('all') // 'all' or scene node index
    const sceneIdx = selectedScene === 'all' ? null : Number(selectedScene)
    const categories = useMemo(() => sceneIdx === null ? production.categories
        : production.categories.map(c => ({ ...c, elements: c.elements.filter(e => e.scenes.includes(sceneIdx)) }))
            .filter(c => c.elements.length > 0),
        [production, sceneIdx])

    const [open, setOpen] = useState({})
    const [categoryModal, setCategoryModal] = useState(null) // {category?}
    const [elementModal, setElementModal] = useState(null)   // {category, element?}
    const [confirm, setConfirm] = useState(null)             // {title, run}

    const refresh = () => setUpdateElements(prev => !prev)
    const result = (err, ok) => { if (err) { toast.error(err); return false } toast.success(ok); refresh(); return true }
    const needScene = () => { if (!scenes.length) { toast.error('Add a scene heading first so production items can be saved with the script.'); return true } return false }

    return <div className='w-full h-full'>
        {categoryModal && <CategoryForm category={categoryModal.category} close={() => setCategoryModal(null)}
            onSave={(data) => {
                const err = categoryModal.category ? renameCustomCategory(editor, categoryModal.category, data) : addCustomCategory(editor, data)
                if (result(err, categoryModal.category ? 'Category updated' : 'Category added')) setCategoryModal(null)
            }} />}
        {elementModal && <ElementForm {...elementModal} categories={production.categories} scenes={scenes} defaultScene={sceneIdx}
            close={() => setElementModal(null)}
            onSave={(data) => {
                let original = elementModal.element || null
                let payload = data
                // New element that already exists elsewhere: reuse it and add the scene link(s).
                if (!original) {
                    const cat = production.categories.find(c => norm(c.name) === norm(data.categoryName))
                    const existing = cat?.elements.find(e => norm(e.name) === norm(data.name))
                    if (existing) {
                        original = existing
                        payload = { ...data, name: existing.name, notes: data.notes || existing.notes,
                            scenes: [...new Set([...existing.scenes, ...data.scenes])].sort((a, b) => a - b) }
                    }
                }
                const err = saveElement(editor, payload, original)
                if (result(err, elementModal.element ? 'Element updated' : 'Element added')) {
                    setOpen(o => ({ ...o, [data.categoryName]: true }))
                    setElementModal(null)
                }
            }} />}
        <Modal open={!!confirm} okText='Delete' closeIcon={false} onCancel={() => setConfirm(null)}
            onOk={() => { confirm.run(); setConfirm(null); refresh() }}>
            {confirm?.title}
        </Modal>

        <BackButton setPanel={setPanel} />
        <div className='w-full h-[calc(100%-65px)] mt-4 shadow-sm overflow-hidden'>
            <div className='bg-[#F3F4F6] h-full rounded flex flex-col pt-2 pb-1'>
                <div className='px-4 pb-2'>
                    <h3 className='font-bold text-[13px]'>Production</h3>
                    <p className='text-[11px] text-[#868686]'>Manage production elements</p>
                </div>
                <hr />
                <div className='px-4 pt-2'>
                    <label htmlFor='production-scene' className='block text-[11px] text-[#868686] mb-1'>Select Scene</label>
                    <select id='production-scene' inputMode='none' value={selectedScene} onChange={e => setSelectedScene(e.target.value)}
                        className='border w-full p-2 rounded-lg bg-white text-[12px] text-[#4b4b4b]'>
                        <option value='all'>All Scenes</option>
                        {scenes.filter(s => s.number).map(s => <option key={s.index} value={s.index}>Scene {s.number} — {s.title}</option>)}
                    </select>
                </div>
                <div className='flex justify-between px-4 py-2 items-center'>
                    <h3 className='font-medium text-[12px] text-[#868686]'>Categories</h3>
                    <button disabled={!permissionToEdit} onClick={() => { if (!needScene()) setCategoryModal({}) }}
                        className='flex items-center gap-1 text-[12px] text-[#868686] hover:text-[#292929] disabled:opacity-50'>
                        <MdAdd size={18} /> Add Category
                    </button>
                </div>
                <div className='px-4 pb-4 overflow-y-auto flex flex-col gap-1 grow'>
                    <Button size='small' icon={<MdAdd size={18} />} disabled={!permissionToEdit}
                        className='self-start shrink-0'
                        onClick={() => { if (!needScene()) setElementModal({ category: production.categories[0] }) }}>
                        Add Element
                    </Button>
                    {sceneIdx !== null && categories.length === 0 &&
                        <p className='text-[12px] text-[#868686] py-3'>No production elements in this scene yet.</p>}
                    {categories.map(cat => {
                        const isOpen = !!open[cat.name]
                        return <div key={cat.name} className='bg-white border rounded-lg overflow-hidden'>
                            <div className='flex items-center'>
                                <div style={{ background: cat.color }} className='self-stretch w-2' />
                                <button onClick={() => setOpen(o => ({ ...o, [cat.name]: !isOpen }))}
                                    className='flex items-center gap-1 grow text-start px-2 py-2 text-[12px] font-medium text-[#4b4b4b] min-w-0'>
                                    {isOpen ? <MdExpandMore size={16} /> : <MdChevronRight size={16} />}
                                    <span className='truncate'>{cat.name}</span>
                                    <span className='text-[#aaa] font-normal'>({cat.elements.length})</span>
                                </button>
                                {!cat.predefined && permissionToEdit && <>
                                    <button title='Edit category' onClick={() => setCategoryModal({ category: cat })} className='p-1 text-[#cacaca] hover:text-[#868686]'><MdEdit size={16} /></button>
                                    <button title='Delete category' onClick={() => setConfirm({ title: `Delete "${cat.name}"? Its elements will move to Other.`, run: () => deleteCustomCategory(editor, cat) })} className='p-1 text-[#cacaca] hover:text-red-400'><MdDelete size={16} /></button>
                                </>}
                                <button title='Add Element' aria-label={`Add element to ${cat.name}`} disabled={!permissionToEdit}
                                    onClick={() => { if (!needScene()) setElementModal({ category: cat }) }}
                                    className='p-1 me-1 text-[#868686] hover:text-[#0099ff] disabled:opacity-40'><MdAdd size={18} /></button>
                            </div>
                            {isOpen && <div className='border-t px-2 py-2 flex flex-col gap-1'>
                                {cat.elements.length === 0 && <p className='text-[11px] text-[#aaa] px-1'>No elements yet</p>}
                                {cat.elements.map(el =>
                                    <div key={el.name} className='group flex items-center gap-2 rounded border px-2 py-1 hover:bg-[#f7f7f7]'>
                                        <button disabled={!permissionToEdit} onClick={() => setElementModal({ category: cat, element: el })} className='grow text-start min-w-0'>
                                            <p className='text-[12px] text-[#4b4b4b] truncate'>{el.name}</p>
                                            <p className='text-[10px] text-[#aaa] truncate'>Scene{el.scenes.length > 1 ? 's' : ''} {el.scenes.map(numberOf).join(', ')}</p>
                                        </button>
                                        {permissionToEdit && <button title='Delete element' onClick={() => setConfirm({ title: `Delete "${el.name}" from every scene?`, run: () => deleteProductionElement(editor, el) })} className='text-[#cacaca] hover:text-red-400'><MdDelete size={16} /></button>}
                                    </div>)}
                            </div>}
                        </div>
                    })}
                </div>
            </div>
        </div>
    </div>
}

export default ProductionPanel
